// ts-rest contract for the Desk Client Book.
//
// The Client Book is the IM's directory of the people they manage. This
// contract is a per-view presentation projection of the Client ontology (now
// shaped like the Dynamics 365 `contact` — see specs/wealth/client.md) plus
// the account/holdings, household and review surfaces it composes. It is NOT a
// domain model; the controller maps `ctx.read(...)` onto these DTOs.
//
// Endpoints under /api/client-book/*:
//   GET  /api/client-book                     — directory rows + summary stats
//   GET  /api/client-book/households          — directory grouped by household
//   GET  /api/client-book/tri-tier            — one row per account umbrella,
//                                               joined up to SUR + primary Contact
//   GET  /api/client-book/:clientId           — full client detail
//   GET  /api/client-book/match-candidates    — suggested duplicate matches
//   POST /api/client-book                     — register a client (create-client)
//   PATCH /api/client-book/:clientId          — edit a client (update-client)
//   POST /api/client-book/:clientId/accounts  — attach a trading account
//   POST /api/client-book/households          — create a household
//   POST /api/client-book/households/:householdId/clients     — add a client
//   POST /api/client-book/households/:householdId/people      — add a related person
//   POST /api/client-book/match/confirm       — confirm a duplicate match (merge)

import { initContract } from '@ts-rest/core';
import { z } from 'zod';

import { terceroClientStatusSchema } from './clients';
import { vulnerabilityAnswersInstrumentSchema } from './vulnerability-assessment';

const c = initContract();

// ---------------------------------------------------------------------------
// Enums
// ---------------------------------------------------------------------------

export const clientSortSchema = z.enum(['aum', 'name', 'segment', 'performance', 'since']);
export type ClientSort = z.infer<typeof clientSortSchema>;

export const clientGroupingSchema = z.enum(['flat', 'household']);
export type ClientGrouping = z.infer<typeof clientGroupingSchema>;

/** Coarse compliance band derived from fact-find expiry vs now. Mirrors the
 * legacy `ComplianceStatus` labels so the existing Pill mapping reuses. */
export const clientComplianceStatusSchema = z.enum([
    'Current',
    'Review Approaching',
    'Overdue',
    'Unknown',
]);
export type ClientComplianceStatus = z.infer<typeof clientComplianceStatusSchema>;

// ---------------------------------------------------------------------------
// Directory row + stats
// ---------------------------------------------------------------------------

export const clientBookRowSchema = z.object({
    clientId: z.string(),
    fullName: z.string(),
    legalEntityType: z.string(),
    segment: z.string(),
    serviceType: z.string(),
    riskRating: z.string(),
    /** Resolved IM display name (from the owning `users` row), or null. */
    investmentManager: z.string().nullable(),
    investmentManagerUserId: z.string().nullable(),
    mandateRef: z.string().nullable(),
    custodian: z.string().nullable(),
    /** Total portfolio value (GBP) from the latest allocation snapshot, or null. */
    totalAum: z.number().nullable(),
    /** Latest period return % from the performance snapshot, or null. */
    performanceYtd: z.number().nullable(),
    complianceStatus: clientComplianceStatusSchema,
    clientSince: z.string().nullable(),
    factFindExpiresAt: z.string().nullable(),
    city: z.string().nullable(),
    postcode: z.string().nullable(),
    sourceSystem: z.string().nullable(),
    /** Household grouping (WS4); null when the client is not in a household. */
    householdId: z.string().nullable(),
    householdName: z.string().nullable(),
});
export type ClientBookRow = z.infer<typeof clientBookRowSchema>;

export const clientBookStatsSchema = z.object({
    totalClients: z.number().int(),
    totalAum: z.number(),
    imCount: z.number().int(),
    kycOverdue: z.number().int(),
});
export type ClientBookStats = z.infer<typeof clientBookStatsSchema>;

export const listClientsResponseSchema = z.object({
    rows: z.array(clientBookRowSchema),
    stats: clientBookStatsSchema,
});
export type ListClientsResponse = z.infer<typeof listClientsResponseSchema>;

// A household with its member rows, for the grouped view.
export const householdGroupSchema = z.object({
    householdId: z.string(),
    householdName: z.string(),
    primaryClientId: z.string().nullable(),
    totalAum: z.number(),
    clients: z.array(clientBookRowSchema),
});
export type HouseholdGroup = z.infer<typeof householdGroupSchema>;

export const listHouseholdsResponseSchema = z.object({
    households: z.array(householdGroupSchema),
    /** Clients not assigned to any household. */
    ungrouped: z.array(clientBookRowSchema),
    stats: clientBookStatsSchema,
});
export type ListHouseholdsResponse = z.infer<typeof listHouseholdsResponseSchema>;

// ---------------------------------------------------------------------------
// Tri-tier row (ADR-0025 Stage 5, PRD §10) — one row per TerceroClient account
// umbrella, joined UP the three-tier client model to its owning Suitability
// Record (via the active `suitability_client` ownership Relationship) and its
// primary Contact (via the SUR's active `contact_suitability` links + §9.4).
//
// This is a SEPARATE presentation projection from `clientBookRowSchema` above
// (which is one row per person `Client`) — NOT a re-grain of it (see the
// tri-tier service + ADR-0025 D9). Rendered as a view-mode on the same Client
// Book page; a Desk read is book-scoped structurally by `ctx.read(TerceroClient)`.
// ---------------------------------------------------------------------------

export const triTierTerceroClientSchema = z.object({
    id: z.string(),
    clientReference: z.string(),
    parentCode: z.string().nullable(),
    /** Canonical wrapper code (DI/D/I/JI/R/L/…); null when unparseable. */
    wrapper: z.string().nullable(),
    status: z.enum(['active', 'orphaned']),
    accountCount: z.number().int(),
    /** Priced value of the umbrella's accounts (GBP), or null when none priced. */
    totalAum: z.number().nullable(),
});
export type TriTierTerceroClient = z.infer<typeof triTierTerceroClientSchema>;

export const triTierSuitabilityRecordSchema = z.object({
    id: z.string(),
    reference: z.string(),
    riskProfileBand: z.string().nullable(),
    status: z.enum(['draft', 'active', 'closed']),
    approved: z.boolean(),
});
export type TriTierSuitabilityRecord = z.infer<typeof triTierSuitabilityRecordSchema>;

export const triTierPrimaryContactSchema = z.object({
    id: z.string(),
    name: z.string(),
    /** The catalogue label of the contact-end role (e.g. "Primary Contact"). */
    roleLabel: z.string(),
});
export type TriTierPrimaryContact = z.infer<typeof triTierPrimaryContactSchema>;

export const triTierClientRowSchema = z.object({
    terceroClient: triTierTerceroClientSchema,
    /** The owning SUR, or null when the umbrella carries no active ownership
     * link (or, for a Desk caller, the link points at another owner's SUR —
     * out of book, so structurally absent). */
    suitabilityRecord: triTierSuitabilityRecordSchema.nullable(),
    /** The SUR's §9.4 primary Contact, or null (no linked contact, or the
     * caller's persona cannot read Contacts — e.g. `view`). */
    primaryContact: triTierPrimaryContactSchema.nullable(),
    /** The umbrella's person-`Client` display name — so a row with no SUR /
     * Contact still says who it is. Null for internal / unmatched umbrellas. */
    clientName: z.string().nullable(),
    /** The same person `Client`'s id, so the row can link through to the
     * Client Book record — where the umbrella's own identity is read, the
     * Clients tier having no detail page. Null for a joint / multi-owner or
     * internal umbrella, which resolves to no single person. */
    clientId: z.string().nullable(),
});
export type TriTierClientRow = z.infer<typeof triTierClientRowSchema>;

export const triTierStatsSchema = z.object({
    /** Total account umbrellas in scope. */
    total: z.number().int(),
    /** Umbrellas carrying an active owning Suitability Record. */
    linked: z.number().int(),
    /** Umbrellas with no owning Suitability Record. */
    unlinked: z.number().int(),
});
export type TriTierStats = z.infer<typeof triTierStatsSchema>;

export const listTriTierResponseSchema = z.object({
    rows: z.array(triTierClientRowSchema),
    stats: triTierStatsSchema,
});
export type ListTriTierResponse = z.infer<typeof listTriTierResponseSchema>;

// ---------------------------------------------------------------------------
// Detail — identity (Dynamics contact shape) + composed sections
// ---------------------------------------------------------------------------

export const clientIdentitySchema = z.object({
    clientId: z.string(),
    fullName: z.string(),
    firstName: z.string().nullable(),
    middleNames: z.string().nullable(),
    lastName: z.string().nullable(),
    title: z.string().nullable(),
    legalEntityType: z.string(),
    typeOfContact: z.string().nullable(),
    corporateRetail: z.string().nullable(),
    fcaClassification: z.string().nullable(),
    serviceType: z.string(),
    segment: z.string(),
    riskRating: z.string(),
    dateOfBirth: z.string().nullable(),
    gender: z.string().nullable(),
    maritalStatus: z.string().nullable(),
    anniversary: z.string().nullable(),
    clientSince: z.string().nullable(),
    email: z.string().nullable(),
    phone: z.string().nullable(),
    addressLine1: z.string().nullable(),
    addressLine2: z.string().nullable(),
    city: z.string().nullable(),
    county: z.string().nullable(),
    postcode: z.string().nullable(),
    country: z.string().nullable(),
    vulnerable: z.boolean().nullable(),
    vulnerableNotes: z.string().nullable(),
    primaryInvestmentObjective: z.string().nullable(),
    assessmentExpiryDate: z.string().nullable(),
    investmentManager: z.string().nullable(),
    investmentManagerUserId: z.string().nullable(),
    terceroClientRef: z.string().nullable(),
    spousePartnerName: z.string().nullable(),
    spousePartnerContactId: z.string().nullable(),
    sourceSystem: z.string().nullable(),
    complianceStatus: clientComplianceStatusSchema,
    staffAccount: z.boolean().nullable(),
    factFindExpiresAt: z.string().nullable(),
    // ── Dynamics contact-form parity (Client Book record form) ──
    // Additional contact channels.
    secondaryEmail: z.string().nullable(),
    paEmail: z.string().nullable(),
    mainPhone: z.string().nullable(),
    homePhone: z.string().nullable(),
    workNumber: z.string().nullable(),
    employer: z.string().nullable(),
    preferredMethodOfContact: z.string().nullable(),
    nationality: z.string().nullable(),
    personalNotes: z.string().nullable(),
    // GPP integration.
    gppClientReference: z.string().nullable(),
    gppBusinessUnit: z.string().nullable(),
    gppClientCategory: z.string().nullable(),
    gppClientType: z.string().nullable(),
    gppCurrency: z.string().nullable(),
    currency: z.string().nullable(),
    nidn: z.string().nullable(),
    // Onboarding checklist.
    creditsafeVerifiedBankAccount: z.boolean().nullable(),
    clientPassedSmartsearch: z.boolean().nullable(),
    leiNiAccurate: z.boolean().nullable(),
    mandateSignedNoFfConflicts: z.boolean().nullable(),
    amlAssessmentComplete: z.boolean().nullable(),
    // ISA-planning detail.
    isaSubscribedThisYear: z.string().nullable(),
    isaSubscriptionAmount: z.number().nullable(),
    regularContributionsRequired: z.string().nullable(),
    bedAndIsaPlanned: z.string().nullable(),
    familyIsaConsiderations: z.string().nullable(),
    juniorIsaRequirements: z.string().nullable(),
    // Fact-find financials.
    complianceRisk: z.string().nullable(),
    riskProfileV4: z.string().nullable(),
    annualIncome: z.number().nullable(),
    occupation: z.string().nullable(),
    totalLiquidNetWorth: z.number().nullable(),
    interestedInNewIssues: z.boolean().nullable(),
    amlRisk: z.string().nullable(),
    // Dynamics fact-find / suitability references (Workstream C). Read-only
    // external refs from the Dynamics export, shown alongside Septima's own
    // native FactFind / SuitabilityAssessment — never overwritten by native data.
    // `lastSuccessfulLoginAt` is the portal last-login, NOT a review date.
    latestFactFindRef: z.string().nullable(),
    latestSuitabilityRef: z.string().nullable(),
    lastFactFindDate: z.string().nullable(),
    lastSuccessfulLoginAt: z.string().nullable(),
});
export type ClientIdentity = z.infer<typeof clientIdentitySchema>;

export const clientMandateSchema = z.object({
    mandateRef: z.string(),
    mandateType: z.string().nullable(),
    effectiveFrom: z.string().nullable(),
    benchmarkId: z.string().nullable(),
});
export type ClientMandate = z.infer<typeof clientMandateSchema>;

export const clientPerformanceSchema = z.object({
    aumGbp: z.number().nullable(),
    asOf: z.string().nullable(),
    periodReturnPct: z.number().nullable(),
    allocation: z
        .array(z.object({ assetClass: z.string(), weightPct: z.number(), valueGbp: z.number() }))
        .default([]),
});
export type ClientPerformance = z.infer<typeof clientPerformanceSchema>;

// One fact-find / suitability assessment occasion (a FactFind version row).
export const clientFactFindSchema = z.object({
    id: z.string(),
    status: z.string().nullable(),
    freshness: z.string().nullable(),
    expiresAt: z.string().nullable(),
    approvedAt: z.string().nullable(),
    atrBand: z.string().nullable(),
    cflBand: z.string().nullable(),
});
export type ClientFactFind = z.infer<typeof clientFactFindSchema>;

export const clientReviewSummarySchema = z.object({
    id: z.string(),
    status: z.string(),
    originTrigger: z.string(),
    dueAt: z.string().nullable(),
    completedAt: z.string().nullable(),
});
export type ClientReviewSummary = z.infer<typeof clientReviewSummarySchema>;

// ── Account / holdings (WS3) ────────────────────────────────────────────────
export const clientHoldingSchema = z.object({
    securityId: z.string().nullable(),
    securityName: z.string(),
    isin: z.string().nullable(),
    quantity: z.number(),
    valueGbp: z.number().nullable(),
    weightPct: z.number().nullable(),
});
export type ClientHolding = z.infer<typeof clientHoldingSchema>;

export const clientCashSchema = z.object({
    currency: z.string(),
    balance: z.number(),
    description: z.string().nullable(),
});
export type ClientCash = z.infer<typeof clientCashSchema>;

export const clientAccountSchema = z.object({
    id: z.string(),
    accountType: z.string(),
    status: z.string().nullable(),
    custodian: z.string().nullable(),
    terceroAccountRef: z.string().nullable(),
    mandateType: z.string().nullable(),
    riskBand: z.string().nullable(),
    openedAt: z.string().nullable(),
    valueGbp: z.number().nullable(),
    performanceYtdPct: z.number().nullable(),
    holdings: z.array(clientHoldingSchema).default([]),
    cash: z.array(clientCashSchema).default([]),
});
export type ClientAccount = z.infer<typeof clientAccountSchema>;

/**
 * A TerceroClient account umbrella this client's portfolios hang under — the
 * bottom tier of the three-tier client model (ADR-0025 Stage 3). Ingest-derived
 * from the ValuationGrid grouping pass and READ-ONLY: the type has no Actions
 * by design, and the tier has no page of its own, so the client record is where
 * an umbrella's identity is read.
 *
 * An umbrella that touches no person Client at all (firm-own / unmatched) has
 * no client record to hang off and is visible only as a row in the tri-tier
 * Client Book; a joint umbrella appears on each of its owners' records.
 */
export const clientUmbrellaSchema = z.object({
    terceroClientId: z.string(),
    /** The raw Client Reference, verbatim, for display. */
    clientReference: z.string(),
    /** "<prefix> <accountNumber>"; null for unparseable references. */
    parentCode: z.string().nullable(),
    prefix: z.string().nullable(),
    accountNumber: z.string().nullable(),
    /** Custodian / SIPP provider — null until the R/L-wrapper default lands. */
    provider: z.string().nullable(),
    /** Wrapper codes across the WHOLE umbrella, so a joint umbrella may list
     * wrappers belonging to a co-owner outside this client's accounts. */
    wrappers: z.array(z.string()),
    /** `orphaned` = the Client Reference vanished from a later grid upload;
     * the row is kept (links may reference it) and badged. */
    status: terceroClientStatusSchema,
    /** Portfolios beneath the umbrella firm-wide — again, may exceed this
     * client's own accounts on a joint umbrella. */
    accountCount: z.number().int(),
    /** The ids of THIS client's accounts that sit under the umbrella, so the
     * account cards below can name the umbrella they belong to. */
    accountIds: z.array(z.string()),
});
export type ClientUmbrella = z.infer<typeof clientUmbrellaSchema>;

// ── Household / family (WS4) ────────────────────────────────────────────────
export const householdMemberSchema = z.object({
    kind: z.enum(['client', 'related']),
    id: z.string(),
    name: z.string(),
    relationship: z.string().nullable(),
    // Free-text description of the relationship (set when relationship is 'other').
    relationshipDescription: z.string().nullable(),
    dateOfBirth: z.string().nullable(),
    aumGbp: z.number().nullable(),
    isPrimary: z.boolean(),
});
export type HouseholdMember = z.infer<typeof householdMemberSchema>;

export const clientHouseholdSchema = z.object({
    id: z.string(),
    name: z.string(),
    members: z.array(householdMemberSchema),
});
export type ClientHousehold = z.infer<typeof clientHouseholdSchema>;

// ── KYC & Vulnerabilities tab summaries ─────────────────────────────────────
// Compact projections of the internal SuitabilityAssessment + Vulnerability
// Assessment rows for a client (the score/band/CFL are recomputed server-side
// from the shared helpers). Surfaced in the client detail so all four staff
// views render the tab from one fetch.
export const clientKycAssessmentSchema = z.object({
    id: z.string(),
    status: z.string(),
    clientType: z.string().nullable(),
    reviewDate: z.string().nullable(),
    riskProfile: z.string().nullable(),
    createdAt: z.string().nullable(),
    completedAt: z.string().nullable(),
});
export type ClientKycAssessment = z.infer<typeof clientKycAssessmentSchema>;

/**
 * One vulnerability assessment row on the "KYC & Vulnerabilities" tab.
 *
 * `totalScore` / `riskBand` are null whenever `answersInstrument` is not
 * `current`: a row answered against the retired pre-SEP-171 instrument keys
 * into no current question and therefore scores 0, and rendering that 0 as a
 * "Green" would turn a record that read "High" yesterday into a reassuring
 * clean bill of health.
 */
export const clientVulnerabilitySummarySchema = z.object({
    id: z.string(),
    status: z.string(),
    totalScore: z.number().nullable(),
    riskBand: z.string().nullable(),
    /** `empty` / `current` / `legacy` — see `classifyVulnerabilityAnswers`. */
    answersInstrument: vulnerabilityAnswersInstrumentSchema,
    createdAt: z.string().nullable(),
    completedAt: z.string().nullable(),
});
export type ClientVulnerabilitySummary = z.infer<typeof clientVulnerabilitySummarySchema>;

export const clientKycSchema = z.object({
    suitabilityAssessments: z.array(clientKycAssessmentSchema).default([]),
    vulnerabilities: z.array(clientVulnerabilitySummarySchema).default([]),
    /**
     * Whether the caller's persona may read the scored vulnerability
     * assessment rows. `restricted` (ops / view — the `VulnerabilityAssessment`
     * Object Type holds Article 9 health data and admits only desk, comply and
     * im-assistant) means `vulnerabilities` is deliberately empty and the tab
     * must say so rather than implying "no vulnerabilities recorded"; the
     * coarse `identity.vulnerable` flag + notes remain the ops signal.
     */
    vulnerabilityAccess: z.enum(['full', 'restricted']).default('full'),
});
export type ClientKyc = z.infer<typeof clientKycSchema>;

/**
 * The Client tier's link to the Contact tier (SEP-177, ADR-0025 D1).
 *
 * `Contact` is authoritative for person and vulnerability data, so the Client
 * record renders its CRM block read-only and points here. Three distinct states
 * the page must tell apart rather than collapsing into one blank panel:
 *
 *   - `crmContactId === null` — the client carries no `dynamics_contact_id`, so
 *     it cannot be matched to a Contact at all (2,264 of 4,644 in prod);
 *   - `crmContactId` set, `contact === null` — either no Contact row carries
 *     that id, or the caller may not read the one that does (another IM's book
 *     under `book-owner` scope, or the View persona, which cannot read Contacts
 *     at all). Deliberately indistinguishable on the wire: saying which would
 *     leak the existence of another IM's record (ADR-0024);
 *   - `contact` set — the cross-link target.
 */
export const clientContactLinkSchema = z.object({
    crmContactId: z.string().nullable(),
    contact: z.object({ contactId: z.string(), name: z.string() }).nullable(),
});
export type ClientContactLink = z.infer<typeof clientContactLinkSchema>;

export const clientDetailSchema = z.object({
    identity: clientIdentitySchema,
    /** Where the frozen CRM block is now maintained (SEP-177). */
    contactLink: clientContactLinkSchema,
    mandate: clientMandateSchema.nullable(),
    performance: clientPerformanceSchema,
    factFinds: z.array(clientFactFindSchema),
    reviews: z.array(clientReviewSummarySchema),
    accounts: z.array(clientAccountSchema),
    /** The account umbrellas this client's portfolios hang under (ADR-0025
     * Stage 3). Empty when the client has no Tercero portfolios. */
    umbrellas: z.array(clientUmbrellaSchema).default([]),
    household: clientHouseholdSchema.nullable(),
    kyc: clientKycSchema,
});
export type ClientDetail = z.infer<typeof clientDetailSchema>;

// ---------------------------------------------------------------------------
// Match candidates (WS5)
// ---------------------------------------------------------------------------

export const matchCandidateSchema = z.object({
    leftClientId: z.string(),
    leftName: z.string(),
    rightClientId: z.string(),
    rightName: z.string(),
    score: z.enum(['high', 'medium']),
    reasons: z.array(z.string()),
});
export type MatchCandidate = z.infer<typeof matchCandidateSchema>;

export const matchCandidatesResponseSchema = z.object({
    candidates: z.array(matchCandidateSchema),
});
export type MatchCandidatesResponse = z.infer<typeof matchCandidatesResponseSchema>;

// ---------------------------------------------------------------------------
// Identity panel — external IDs (Workstream A)
// ---------------------------------------------------------------------------

// One external identifier crosswalked to this client (a ClientExternalId row).
// Read-only; `idType` / `system` are closed enum/domain VALUES (snake_case is
// the stored value, NOT a key) and rendered verbatim by the panel.
export const clientExternalIdSchema = z.object({
    id: z.string(),
    system: z.string(),
    idType: z.string(),
    value: z.string(),
    label: z.string().nullable(),
    firstSeenAt: z.string(),
    resolvedAt: z.string().nullable(),
});
export type ClientExternalIdDto = z.infer<typeof clientExternalIdSchema>;

export const clientExternalIdsResponseSchema = z.object({
    // The canonical Septima client id (ontology_clients.id).
    septimaId: z.string(),
    externalIds: z.array(clientExternalIdSchema),
});
export type ClientExternalIdsResponse = z.infer<typeof clientExternalIdsResponseSchema>;

// ---------------------------------------------------------------------------
// Request bodies (mutations — WS2 register, WS3 accounts, WS4 households, WS5)
// ---------------------------------------------------------------------------

export const createClientRequestSchema = z.object({
    firstName: z.string().min(1),
    lastName: z.string().min(1),
    middleNames: z.string().optional(),
    title: z.string().optional(),
    legalEntityType: z
        .enum([
            'Individual',
            'JointAccount',
            'Trust',
            'Pension',
            'Charity',
            'Corporate',
            'Partnership',
        ])
        .default('Individual'),
    serviceType: z.enum(['Discretionary', 'Advisory', 'ExecutionOnly']).default('Discretionary'),
    segment: z
        .enum(['MassAffluent', 'HighNetWorth', 'UltraHighNetWorth', 'Professional'])
        .default('MassAffluent'),
    riskRating: z
        .enum(['Conservative', 'Cautious', 'Balanced', 'Growth', 'Adventurous'])
        .default('Balanced'),
    dateOfBirth: z.string().optional(),
    gender: z.string().optional(),
    maritalStatus: z.string().optional(),
    email: z.string().optional(),
    phone: z.string().optional(),
    addressLine1: z.string().optional(),
    addressLine2: z.string().optional(),
    city: z.string().optional(),
    county: z.string().optional(),
    postcode: z.string().optional(),
    country: z.string().optional(),
    investmentManagerUserId: z.string().optional(),
    clientSince: z.string().optional(),
    // Classification + suitability anchors editable from the record form.
    typeOfContact: z.enum(['Person', 'Company']).optional(),
    corporateRetail: z.enum(['Corporate', 'Retail']).optional(),
    fcaClassification: z.enum(['Retail', 'Professional', 'EligibleCounterparty']).optional(),
    anniversary: z.string().optional(),
    spousePartnerName: z.string().optional(),
    primaryInvestmentObjective: z.string().optional(),
    // Dynamics contact-form parity (manual-fill from the Client Book record form).
    secondaryEmail: z.string().optional(),
    paEmail: z.string().optional(),
    mainPhone: z.string().optional(),
    homePhone: z.string().optional(),
    workNumber: z.string().optional(),
    employer: z.string().optional(),
    preferredMethodOfContact: z.string().optional(),
    nationality: z.string().optional(),
    personalNotes: z.string().optional(),
    gppClientReference: z.string().optional(),
    gppBusinessUnit: z.string().optional(),
    gppClientCategory: z.string().optional(),
    gppClientType: z.string().optional(),
    gppCurrency: z.string().optional(),
    currency: z.string().optional(),
    nidn: z.string().optional(),
    creditsafeVerifiedBankAccount: z.boolean().optional(),
    clientPassedSmartsearch: z.boolean().optional(),
    leiNiAccurate: z.boolean().optional(),
    mandateSignedNoFfConflicts: z.boolean().optional(),
    amlAssessmentComplete: z.boolean().optional(),
    isaSubscribedThisYear: z.string().optional(),
    isaSubscriptionAmount: z.number().nullable().optional(),
    regularContributionsRequired: z.string().optional(),
    bedAndIsaPlanned: z.string().optional(),
    familyIsaConsiderations: z.string().optional(),
    juniorIsaRequirements: z.string().optional(),
    complianceRisk: z.string().optional(),
    riskProfileV4: z.string().optional(),
    annualIncome: z.number().nullable().optional(),
    occupation: z.string().optional(),
    totalLiquidNetWorth: z.number().nullable().optional(),
    interestedInNewIssues: z.boolean().optional(),
    amlRisk: z.string().optional(),
});
export type CreateClientRequest = z.infer<typeof createClientRequestSchema>;

export const updateClientRequestSchema = createClientRequestSchema.partial();
export type UpdateClientRequest = z.infer<typeof updateClientRequestSchema>;

export const registerAccountRequestSchema = z.object({
    accountType: z.enum(['ISA', 'JISA', 'GIA', 'SIPP', 'Bond', 'Trust', 'Other']),
    custodian: z.string().optional(),
    terceroAccountRef: z.string().optional(),
    status: z.enum(['open', 'closed', 'suspended']).default('open'),
    mandateType: z.string().optional(),
    riskBand: z.string().optional(),
    openedAt: z.string().optional(),
});
export type RegisterAccountRequest = z.infer<typeof registerAccountRequestSchema>;

export const createHouseholdRequestSchema = z.object({
    name: z.string().min(1),
    primaryClientId: z.string().optional(),
    notes: z.string().optional(),
});
export type CreateHouseholdRequest = z.infer<typeof createHouseholdRequestSchema>;

export const addClientToHouseholdRequestSchema = z.object({
    clientId: z.string().min(1),
    // The linked client's role within the household (relative to the lead
    // client). Optional — null/omitted leaves the member untagged.
    relationship: z.enum(['spouse', 'partner', 'child', 'parent', 'dependant', 'other']).optional(),
    // Free-text description of the relationship, collected when relationship is
    // 'other'. Optional for the other enum values.
    description: z.string().optional(),
});
export type AddClientToHouseholdRequest = z.infer<typeof addClientToHouseholdRequestSchema>;

export const addRelatedPersonRequestSchema = z.object({
    firstName: z.string().min(1),
    lastName: z.string().min(1),
    relationship: z.enum(['spouse', 'partner', 'child', 'parent', 'dependant', 'other']),
    dateOfBirth: z.string().optional(),
    linkedClientId: z.string().optional(),
    // Free-text description of the relationship, collected when relationship is
    // 'other'. Optional for the other enum values.
    description: z.string().optional(),
});
export type AddRelatedPersonRequest = z.infer<typeof addRelatedPersonRequestSchema>;

export const confirmMatchRequestSchema = z.object({
    canonicalClientId: z.string().min(1),
    duplicateClientId: z.string().min(1),
});
export type ConfirmMatchRequest = z.infer<typeof confirmMatchRequestSchema>;

const okClient = z.object({ ok: z.boolean(), clientId: z.string() });
const okGeneric = z.object({ ok: z.boolean(), id: z.string().optional() });
import { errorBodyWithCode as errorBody } from '../schemas/common';

// ---------------------------------------------------------------------------
// Contract
// ---------------------------------------------------------------------------

export const clientBookContract = c.router(
    {
        listClients: {
            method: 'GET',
            path: '/client-book',
            query: z
                .object({
                    search: z.string().optional(),
                    sort: clientSortSchema.optional(),
                    segment: z.string().optional(),
                    serviceType: z.string().optional(),
                    investmentManagerUserId: z.string().optional(),
                })
                .optional(),
            responses: { 200: listClientsResponseSchema },
            summary: 'Client directory rows + summary stats',
        },
        listHouseholds: {
            method: 'GET',
            path: '/client-book/households',
            responses: { 200: listHouseholdsResponseSchema },
            summary: 'Client directory grouped by household',
        },
        listTriTier: {
            method: 'GET',
            path: '/client-book/tri-tier',
            query: z
                .object({
                    search: z.string().optional(),
                })
                .optional(),
            responses: { 200: listTriTierResponseSchema },
            summary: 'Account umbrellas joined up to Suitability Record + primary Contact',
        },
        matchCandidates: {
            method: 'GET',
            path: '/client-book/match-candidates',
            responses: { 200: matchCandidatesResponseSchema },
            summary: 'Suggested duplicate-client matches for IM confirmation',
        },
        getDetail: {
            method: 'GET',
            path: '/client-book/:clientId',
            responses: { 200: clientDetailSchema, 404: errorBody },
            summary: 'Full client detail: identity, accounts, household, fact-finds, reviews',
        },
        getExternalIds: {
            method: 'GET',
            path: '/client-book/:clientId/external-ids',
            responses: { 200: clientExternalIdsResponseSchema, 404: errorBody },
            summary: 'The client’s external IDs from the identity registry (read-only)',
        },
        createClient: {
            method: 'POST',
            path: '/client-book',
            body: createClientRequestSchema,
            responses: { 200: okClient, 400: errorBody },
            summary: 'Register a new client (create-client Action)',
        },
        updateClient: {
            method: 'PATCH',
            path: '/client-book/:clientId',
            body: updateClientRequestSchema,
            responses: { 200: okClient, 400: errorBody, 404: errorBody },
            summary: 'Edit a client (update-client Action)',
        },
        registerAccount: {
            method: 'POST',
            path: '/client-book/:clientId/accounts',
            body: registerAccountRequestSchema,
            responses: { 200: okGeneric, 400: errorBody, 404: errorBody },
            summary: 'Attach a trading account to a client (register-client-account Action)',
        },
        createHousehold: {
            method: 'POST',
            path: '/client-book/households',
            body: createHouseholdRequestSchema,
            responses: { 200: okGeneric, 400: errorBody },
            summary: 'Create a household group',
        },
        addClientToHousehold: {
            method: 'POST',
            path: '/client-book/households/:householdId/clients',
            body: addClientToHouseholdRequestSchema,
            responses: { 200: okGeneric, 400: errorBody, 404: errorBody },
            summary: 'Add a client to a household',
        },
        addRelatedPerson: {
            method: 'POST',
            path: '/client-book/households/:householdId/people',
            body: addRelatedPersonRequestSchema,
            responses: { 200: okGeneric, 400: errorBody, 404: errorBody },
            summary: 'Add a non-client family member to a household',
        },
        confirmMatch: {
            method: 'POST',
            path: '/client-book/match/confirm',
            body: confirmMatchRequestSchema,
            responses: { 200: okGeneric, 400: errorBody, 404: errorBody },
            summary: 'Confirm a duplicate match — collapse into one canonical client',
        },
    },
    {
        pathPrefix: '/api',
        strictStatusCodes: false,
    },
);
